import { useCallback, useMemo, useState } from "react";
import { IFilterDropdownProps } from "./FilterDropdown";
import { IFilterItemV2 } from "./types";
import { useSearchParams } from "react-router-dom";

interface IUseFilterProps {
  label: string;
  selectionList: IFilterItemV2[];
  options?: {
    searchParamKey?: string;
    sideEffectOnChange?: Function;
    hiddenFilter?: boolean;
    itemLabel?: "default" | "badge";
  };
  isLoading?: boolean;
  multiple?: boolean;
}

export const useFilter = ({
  label,
  selectionList,
  options,
  isLoading = false,
  multiple = false,
}: IUseFilterProps): {
  value: string | undefined;
  renderProps: IFilterDropdownProps;
} => {
  const [selectedState, setSelectedState] = useState<IFilterItemV2[]>([]);

  const [searchParams, setSearchParams] = useSearchParams();

  const onChangeSelection = useCallback(
    (newSelection: IFilterItemV2[]) => {
      setSelectedState(newSelection);

      setSearchParams((params) => {
        if (options && options.searchParamKey) {
          if (
            newSelection.length > 0 &&
            newSelection.map((item) => item.value).join(",").length > 0
          ) {
            params.set(
              options.searchParamKey,
              newSelection.map((item) => item.value).join(",")
            );
          } else {
            params.delete(options.searchParamKey);
          }
        }
        return params;
      });

      if (options && options.sideEffectOnChange) {
        options.sideEffectOnChange();
      }
    },
    [options, setSearchParams]
  );

  const finalSelected: IFilterItemV2[] = useMemo(() => {
    if (options && options.searchParamKey) {
      const getSearchParamKey = searchParams.get(options.searchParamKey);

      const valueListFromParams =
        getSearchParamKey === null
          ? []
          : multiple
            ? getSearchParamKey.split(",")
            : [getSearchParamKey];

      // Aggregate items from selection list to values on params
      const itemsMatchedFromSelectionList = selectionList.filter((item) => {
        return (
          item.value !== undefined && valueListFromParams.includes(item.value)
        );
      });

      // Items that are added to params but not on selection list (or if selection list is not loaded)
      const fallbackItems = valueListFromParams
        .filter(
          (item) =>
            !itemsMatchedFromSelectionList.map((v) => v.value).includes(item)
        )
        .map((item) => ({
          key: item,
          value: item,
          getLabel: () => item,
        }));

      // Combine aggregated list with fallback list
      return [...itemsMatchedFromSelectionList, ...fallbackItems];
    }

    // If there's no params, just get what is saved on the states
    return selectedState;
  }, [options, searchParams, selectionList, selectedState, multiple]);

  const finalValue = useMemo(() => {
    const joinedValue = finalSelected.map((item) => item.value).join(",");
    // There can still be a case that all selected item values are empty strings
    return joinedValue ? joinedValue : undefined;
  }, [finalSelected]);

  const renderProps = useMemo(
    () =>
      ({
        type: options && options.hiddenFilter ? "hidden" : "normal",
        label,
        options: selectionList,
        selected: finalSelected,
        onChangeSelection,
        disabled: selectionList.length === 0,
        isLoading,
        multiple,
        itemLabel: options && options.itemLabel,
      }) satisfies IFilterDropdownProps,
    [
      label,
      selectionList,
      finalSelected,
      onChangeSelection,
      isLoading,
      multiple,
      options,
    ]
  );

  return { value: finalValue, renderProps };
};
